import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import activityGames from '../data/activityGames.js';
import { getActivityBank } from '../lib/activityBank.js';
import { canEdit, isProjectAdmin } from '../lib/permissions.js';
import { formatDate, generateId, habilidadeLabels, todayISO } from '../lib/utils.js';
import { buildDeck, resultScore } from '../lib/memoryGame.js';

function visibleStudents(data, user) {
  if (!user || isProjectAdmin(user)) return [];
  if (user.role === 'school_admin') return (data.students || []).filter((student) => student.schoolId === user.schoolId);
  if (user.role === 'professional') return (data.students || []).filter((student) => student.schoolId === user.schoolId && student.profissionais?.includes(user.uid));
  return (data.students || []).filter((student) => student.schoolId === user.schoolId && student.responsaveis?.includes(user.uid));
}

function schoolOf(data, schoolId) {
  return (data.schools || []).find((school) => school.id === schoolId) || {};
}

function teacherFor(data, user, student) {
  if (user?.role === 'professional' || user?.role === 'school_admin') return user;
  const teacherId = student?.profissionais?.[0];
  return (data.users || []).find((item) => item.uid === teacherId) || {};
}

function MemoryGame({ game, activity, student, user, data, setData, onSave }) {
  const editable = canEdit(user);
  const [level, setLevel] = useState(game.nivelInicial || 2);
  const [deck, setDeck] = useState(() => buildDeck(game, game.nivelInicial || 2));
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [errors, setErrors] = useState(0);
  const [hints, setHints] = useState(0);
  const [startedAt, setStartedAt] = useState(() => Date.now());
  const [finishedAt, setFinishedAt] = useState(null);
  const [message, setMessage] = useState('Escolha duas cartas para encontrar o par.');

  const pairCount = deck.length / 2;
  const matchedCount = matched.length;
  const completed = matchedCount === pairCount;
  const elapsedSeconds = Math.max(1, Math.round(((finishedAt || Date.now()) - startedAt) / 1000));
  const score = resultScore({ pairCount, matchedCount, attempts, errors, hints });

  function reset(nextLevel = level) {
    setLevel(Number(nextLevel));
    setDeck(buildDeck(game, Number(nextLevel)));
    setFlipped([]);
    setMatched([]);
    setAttempts(0);
    setErrors(0);
    setHints(0);
    setStartedAt(Date.now());
    setFinishedAt(null);
    setMessage('Nova rodada iniciada. Escolha duas cartas.');
  }

  function choose(card) {
    if (finishedAt || matched.includes(card.pairId) || flipped.some((item) => item.uid === card.uid) || flipped.length >= 2) return;
    const nextFlipped = [...flipped, card];
    setFlipped(nextFlipped);
    if (nextFlipped.length === 1) {
      setMessage('Agora escolha a carta que forma o par.');
      return;
    }

    setAttempts((value) => value + 1);
    const [first, second] = nextFlipped;
    if (first.pairId === second.pairId) {
      const nextMatched = [...matched, first.pairId];
      setMatched(nextMatched);
      setFlipped([]);
      if (nextMatched.length === pairCount) {
        setFinishedAt(Date.now());
        setMessage('Rodada concluída. Confira o resultado e registre no atendimento.');
      } else {
        setMessage('Par encontrado. Continue a rodada.');
      }
      return;
    }

    setErrors((value) => value + 1);
    setMessage('Ainda não foi dessa vez. Observe novamente e tente outro par.');
    window.setTimeout(() => setFlipped([]), 700);
  }

  function giveHint() {
    const missing = deck.find((card) => !matched.includes(card.pairId));
    if (!missing || completed) return;
    setHints((value) => value + 1);
    setMessage(`Pista: procure o par de "${missing.rotulo}".`);
  }

  function registerResult() {
    if (!editable || !student || !activity) return;
    const metrics = {
      pares_encontrados: matchedCount,
      pares_total: pairCount,
      tentativas: attempts,
      erros: errors,
      pistas: hints,
      tempo_segundos: elapsedSeconds,
      score,
    };
    const desempenho = score >= 80 ? 'conseguiu' : score >= 40 ? 'parcial' : 'nao_conseguiu';
    const log = {
      id: generateId('log'),
      studentId: student.id,
      data: todayISO(),
      autorUid: user.uid,
      atividadeId: activity.id,
      gameId: game.id,
      objetivo: activity.objetivo,
      habilidades: game.habilidades_alvo,
      habilidades_alvo: game.habilidades_alvo,
      score: score / 100,
      // itens[] é o formato que evolutionAnalytics.logSeries lê para alimentar o
      // gráfico de desempenho dos atendimentos — sem isso a sessão de jogo fica
      // registrada no atendimento mas não aparece na evolução do aluno.
      itens: [{ atividadeId: activity.id, titulo: game.titulo, habilidades: game.habilidades_alvo, resultado: desempenho, score: score / 100, obs: `pares ${matchedCount}/${pairCount}, tentativas ${attempts}, erros ${errors}, pistas ${hints}` }],
      estrategia: `${game.titulo}\nAtividade base: ${activity.titulo}\nMétricas: pares ${matchedCount}/${pairCount}, tentativas ${attempts}, erros ${errors}, pistas ${hints}, tempo ${elapsedSeconds}s, score ${score}/100.`,
      observacao: completed ? 'Jogo interativo concluído e registrado automaticamente.' : 'Jogo interativo registrado antes da conclusão para acompanhamento do professor.',
      resultado: {
        tipo: 'jogo_interativo',
        gameId: game.id,
        atividadeId: activity.id,
        metricas: metrics,
        registradoEm: new Date().toISOString(),
      },
    };
    const next = { ...data, logs: [log, ...(data.logs || [])] };
    setData(next);
    onSave?.(next);
    setMessage('Resultado registrado no atendimento do aluno.');
  }

  return <section className='card game-shell'>
    <div className='game-toolbar'>
      <label>Nível
        <select value={level} onChange={(event) => reset(event.target.value)}>
          <option value='1'>1 - introdutório</option>
          <option value='2'>2 - intermediário</option>
          <option value='3'>3 - ampliado</option>
        </select>
      </label>
      <div className='actions'>
        <button type='button' onClick={giveHint}>Dar pista</button>
        <button type='button' onClick={() => reset(level)}>Reiniciar jogo</button>
      </div>
    </div>

    <div className='game-status' role='status' aria-live='polite'>{message}</div>

    <div className='memory-board'>
      {deck.map((card) => {
        const isVisible = matched.includes(card.pairId) || flipped.some((item) => item.uid === card.uid);
        return <button
          type='button'
          className={`memory-card${isVisible ? ' open' : ''}${matched.includes(card.pairId) ? ' matched' : ''}`}
          key={card.uid}
          onClick={() => choose(card)}
          aria-label={isVisible ? card.rotulo : 'Carta fechada'}
        >
          <span>{isVisible ? card.simbolo : '?'}</span>
          {isVisible && <small>{card.rotulo}</small>}
        </button>;
      })}
    </div>

    <div className='game-metrics'>
      <div><b>{matchedCount}/{pairCount}</b><span>Pares encontrados</span></div>
      <div><b>{attempts}</b><span>Tentativas</span></div>
      <div><b>{errors}</b><span>Erros</span></div>
      <div><b>{hints}</b><span>Pistas</span></div>
      <div><b>{elapsedSeconds}s</b><span>Tempo</span></div>
      <div><b>{score}/100</b><span>Score</span></div>
    </div>

    <div className='actions'>
      {editable && student && <button type='button' onClick={registerResult}>Registrar resultado no atendimento</button>}
      {student && <Link className='action-link' to={`/aluno/${student.id}/atividades`}>Voltar às atividades do aluno</Link>}
    </div>
  </section>;
}

export function GamePage({ user, data, setData, onSave }) {
  const { gameId } = useParams();
  const [searchParams] = useSearchParams();
  const game = activityGames.find((item) => item.id === gameId);
  const bank = useMemo(() => getActivityBank(data), [data]);
  const students = useMemo(() => visibleStudents(data, user), [data, user]);
  const queryStudentId = searchParams.get('studentId') || '';
  const initialStudentId = students.some((student) => student.id === queryStudentId) ? queryStudentId : (students[0]?.id || '');
  const [studentId, setStudentId] = useState(initialStudentId);
  const student = students.find((item) => item.id === studentId);
  const activity = game ? bank.atividades.find((item) => item.id === game.activityId) : null;
  const school = schoolOf(data, student?.schoolId || user?.schoolId);
  const teacher = teacherFor(data, user, student);

  if (!game || !activity) {
    return <section className='card'><h1>Jogo não encontrado</h1><p className='hint'>Volte ao catálogo e escolha um jogo disponível.</p><Link className='action-link' to='/catalogo'>Voltar ao catálogo</Link></section>;
  }

  return <>
    <header className='header'>
      <div>
        <p>atividade interativa</p>
        <h1>{game.titulo}</h1>
        <span>{game.subtitulo}</span>
      </div>
    </header>

    <section className='card activity-sheet'>
      <div className='activity-meta-grid'>
        <div><b>Escola</b><span>{school?.nome || '-'}</span></div>
        <div><b>Professor</b><span>{teacher?.nome || '-'}</span></div>
        <div><b>Aluno</b><span>{student?.nome || 'Selecione um aluno'}</span></div>
        <div><b>Data</b><span>{formatDate(todayISO())}</span></div>
      </div>

      {students.length > 0 && <label className='student-picker'>Aluno para registrar o resultado
        <select value={studentId} onChange={(event) => setStudentId(event.target.value)}>
          {students.map((item) => <option value={item.id} key={item.id}>{item.nome} — {item.serie}</option>)}
        </select>
      </label>}

      <div className='activity-detail'>
        <b>Atividade base:</b> {activity.titulo}<br />
        <b>Objetivo:</b> {activity.objetivo}<br />
        <b>Habilidades:</b> {(game.habilidades_alvo || []).map((id) => habilidadeLabels[id] || id).join(', ')}<br />
        <b>Métricas:</b> {(game.metricas || []).join(', ')}
      </div>

      <h2>Adaptações sugeridas</h2>
      <ul>{(game.adaptacoes || []).map((item) => <li key={item}>{item}</li>)}</ul>
    </section>

    <MemoryGame game={game} activity={activity} student={student} user={user} data={data} setData={setData} onSave={onSave} />
  </>;
}
